import moment from "moment"

import { stringifyDate } from "../shared_components/utils"

export const RUN_DETAIL_FIELDS = [
  {
    key: "alias",
    label: "name",
    type: "string",
  },
  { key: "observation_min", label: "Earliest Data", type: "date" },
  { key: "observation_max", label: "Latest Data", type: "date" },
  { key: "run_length", label: "Run Length", type: "string" },
  {
    key: "run_description",
    label: "Run Description",
    type: "string",
  },
  {
    key: "run_comment",
    label: "Run Comment",
    type: "string",
  },
  {
    key: "started_by",
    label: "Started By",
    type: "user_obj",
    specifics: ["first_name", "last_name", "username"],
  },
  {
    key: "started_on",
    label: "Started On",
    type: "date",
  },
  {
    key: "inoculated_on",
    label: "Inoculated On",
    type: "date",
  },
  {
    key: "ended_by",
    label: "Ended By",
    type: "user_obj",
    specifics: ["first_name", "last_name", "username"],
  },
  {
    key: "finished_on",
    label: "Finished On",
    type: "date",
  },
  ...["biocatalyst_parents", "bioCatalystParents"].map((key) => ({
    key: key,
    label: "Biocatalyst Parent(s)",
    type: "biocat_parent",
    specifics: ["name", "lot_no", "count", "uom"],
  })),
  ...["run_parents", "runParents"].map((key) => ({
    key: key,
    label: "Run Parent(s)",
    type: "run_parent",
    specifics: ["name"],
  })),
  ...["external_runs", "externalRuns"].map((key) => ({
    key: key,
    label: "External Run Parent(s)",
    type: "external_run",
    specifics: ["name_source", "name"],
  })),
  ...["external_samples", "externalSamples"].map((key) => ({
    key: key,
    label: "External Sample Parent(s)",
    type: "external_sample",
    specifics: ["name_source", "name"],
  })),
]

const parentJoiner = ", "

export const getParentLinks = (key, val) => {
  const linkOuts = []
  if (["externalRuns", "external_runs", "externalSamples", "external_samples"].includes(key) && val) {
    const isRun = ["externalRuns", "external_runs"].includes(key)
    const bcLabel = "BioControl: "
    val
      .split(parentJoiner)
      .filter((entry) => entry.includes(bcLabel))
      .map((entry) => {
        const parentName = entry.replace(bcLabel, "").trim()
        linkOuts.push([parentName, `http://ilapp01.lt.local/${isRun ? "Runs" : "Samples"}/${parentName}`])
      })
  } else if (["run_parents", "runParents"].includes(key) && val) {
    val.split(parentJoiner).map((entry) => {
      linkOuts.push([entry, `/fermentation-management/run/${encodeURIComponent(entry)}/`])
    })
  }
  return linkOuts
}

export const getExperimentStrings = (experiments) =>
  (experiments || []).map(
    (e) => `${e.name} [[${e.project_netsuite_project_id}] ${e.project_name} - ${e.project_task_name}]`,
  )

export const getRunDetailValue = (row, detail_type) => {
  const detailReference = row?.[detail_type.key]
  if (!detailReference) return null
  if (detail_type.type === "string") {
    // detailReference is a string
    return `${detailReference}`
  } else if (detail_type.type === "date") {
    return stringifyDate(detailReference)
  } else if (["user_obj"].includes(detail_type.type)) {
    // detailReference is an object
    let value_arr = []
    detail_type.specifics.forEach((entry) => {
      let detail = detailReference?.[entry] || ""
      if (detail && entry === "username") {
        detail = `(${detail})`
      }
      detail && value_arr.push(detail)
    })
    return value_arr.length > 0 ? value_arr.join(" ") : null
  } else if (["biocat_parent", "run_parent", "external_run", "external_sample"].includes(detail_type.type)) {
    // detailReference is a list
    if (!detailReference || detailReference.length === 0) return null

    let value_arr = []
    detailReference.forEach((parent) => {
      let parentDetails = []
      detail_type.specifics.forEach((entry) => {
        let detail = parent?.[entry] || ""
        if (detail && entry === "lot_no") {
          detail = `(${detail})`
        }
        if (detail && entry === "name_source") {
          detail = `${detail}:`
        }
        detail && parentDetails.push(`${detail}`)
      })
      parentDetails.length > 0 && value_arr.push(parentDetails.join(" "))
    })
    return value_arr.length > 0 ? value_arr.join(parentJoiner) : null
  }
}

export const table_icons = {
  paginationSwitchDown: "fa fa-solid fa-caret-down fa-lg",
  paginationSwitchUp: "fa fa-solid fa-caret-up fa-lg",
  refresh: "fa fa-refresh",
  toggle: "fa fa-list-alt",
  columns: "fa fa-th icon-th",
  detailOpen: "fa fa-solid fa-caret-down fa-lg",
  detailClose: "fa fa-solid fa-caret-up fa-lg",
}

export const basicTableSetup = (url, columns, extra_settings = {}) => {
  return {
    url: url,
    showRefresh: false,
    idField: "id",
    pagination: true,
    sidePagination: "server",
    pageList: [5, 10, 20, 50, 100, 200, 500, 1000],
    classes: "table-hover",
    detailView: true,
    striped: true,
    paginationVAlign: "both",
    explicitlyManageSelectAll: true,
    icons: table_icons,
    columns: columns,
    ...extra_settings,
  }
}

export function addErrorListener() {
  window.addEventListener(
    "error",
    function (ev) {
      const err = ev.error || ev.message
      console.error(ev.message)
      if (window.lanzacofee_log_js_error) {
        let message = err ? err.toString() : "No data"
        if (err && err instanceof Error && err.stack) {
          message = err.stack
        }

        window.lanzacofee_log_js_error(message)
      }
    },
    false,
  )
}

export function onCheckAll(e, rows, selections, table_class_name, updatesDisabled = false) {
  if (!updatesDisabled) {
    if (e.type === "uncheck-all") {
      selections = []
    } else {
      let ids = $.map(!$.isArray(rows) ? [rows] : rows, function (row) {
        return row.id
      })
      var func = $.inArray(e.type, ["check", "check-all"]) > -1 ? "union" : "difference"
      selections = window._[func](selections, ids)
    }

    $(`.${table_class_name}`).bootstrapTable("checkSelectAllButton", selections.length > 0)
  }

  return selections
}

/* Status handling.  As well as managing the error banner, this does a redirect-on-auth-error
 * analogous to what llFetch does in the main graphing code. */
const REDIRECT_STATUS_KEY = "lanzacofee-attempted-authentication-redirect.bs-table"

function setErrorBanner(error) {
  $("#error-banner").text(error)
}

export function onLoadError(ev, status, res) {
  if (status === 401) {
    if (!localStorage[REDIRECT_STATUS_KEY]) {
      localStorage[REDIRECT_STATUS_KEY] = true
      location.reload()
    } else {
      console.error("Giving up because redirect has already been tried")
    }
  }
  setErrorBanner(`Error fetching data: ${res.statusText}`)
}

export function onLoadSuccess(table_class_name, selections) {
  localStorage.removeItem(REDIRECT_STATUS_KEY)
  setErrorBanner(null)
  $(`.${table_class_name}`).bootstrapTable("checkSelectAllButton", selections.length > 0)
}

export function tableDateFormatter(value, _row, _index) {
  return value && moment(new Date(value)).format("YYYY-MM-DD HH:mm:ss")
}

export const getTableTranslations = (intl, table_name, translation_mapping) => {
  /*
    Shared between Run Table and Files Table

    A bit of an odd work-around due to the bootstrap table being created/managed outside of React. Using jQuery to grab
    HTML elements in order to translate them. Long-term we should shift table creation/management into React.
  */

  const translateText = (element, translation_key) => {
    if (translation_key && Object.keys(translation_mapping).includes(translation_key)) {
      $(element).text(
        intl.formatMessage({
          id: translation_mapping[translation_key]["id"],
          defaultMessage: translation_mapping[translation_key]["default"],
        }),
      )
    }
  }

  // Accessing UI elements made by Bootstrap table in view_entry for translation
  // Column Headers
  $(`thead > tr > .${table_name}__translate_column > .th-inner`).each((i, element) => {
    const translation_key = $(element).closest(`.${table_name}__translate_column`).attr("data-field")
    translateText(element, translation_key)
  })

  // Pagination Information
  // $(".fixed-table-pagination > .pagination-detail > .page-list").each((i, element) => {
  //   console.error(element)
  // });
  // $(".fixed-table-pagination > .pagination-detail > .pagination-info").each((i, element) => {
  // 	console.error(element);
  // });
  // END Pagination Information

  // Detail Information
  $("tbody > .detail-view > td > .row > .col-sm-2").each((i, element) => {
    const translation_key = $(element).attr("data-field")
    translateText(element, translation_key)
  })
  // END Accessing UI elements made by Bootstrap table in view_entry for translation
}
